import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { AsButtonDirective } from '@directives/as-button/as-button.directive';
import { PersonItem, PersonSearchOptions } from '@models/custom-fields.model';
import {
  OrderActionParams,
  OrderActionType,
  OrderCenterAction,
  OrderCenterResponse
} from '@models/dashboard.model';
import { SelectOrderForItem } from '@models/order-for-others.model';
import { NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { DashboardService } from '@services/dashboard/dashboard.service';
import { ModalService } from '@services/modal/modal.service';
import { PersonService } from '@services/person/person.service';
import { SessionService } from '@services/session/session.service';
import { TranslationService } from '@services/translation/translation.service';
import { ListboxComponent } from '@shared-components/listbox/listbox.component';
import { PersonCardComponent } from '@shared-components/person-card/person-card.component';
import {
  Observable,
  OperatorFunction,
  Subscription,
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  of,
  switchMap,
  tap
} from 'rxjs';
import { DataLoaderDirective, TdsTypeAheadComponent } from 'tangoe-design-system';

export const ORDER_FOR_OPTIONS: SelectOrderForItem[] = [
  {
    name: 'Myself',
    icon: 'person'
  }
];

@Component({
  selector: 'ts-select-order-for',
  templateUrl: './select-order-for.component.html',
  imports: [
    CommonModule,
    TranslationPipe,
    AsButtonDirective,
    DataLoaderDirective,
    ListboxComponent,
    PersonCardComponent,
    TdsTypeAheadComponent
  ]
})
export class SelectOrderForComponent implements OnInit, OnDestroy {
  @ViewChild('selectOrderForTemplate') select_order_for_template: TemplateRef<any>;
  @ViewChild('selectPersonTemplate') select_person_template: TemplateRef<any>;
  @ViewChild('selectActivityTemplate') select_activity_template: TemplateRef<any>;
  order_for: string;
  ofo_enabled: boolean;
  person_not_found: string;
  loading: boolean = false;
  no_activity_found: boolean = false;
  order_center_action: OrderCenterAction;
  my_order_center_actions: OrderCenterAction[];
  step_id: FormControl = new FormControl<number | null>(null);
  order_for_options: SelectOrderForItem[] = ORDER_FOR_OPTIONS;
  person: FormControl = new FormControl(null);
  order_action_type: EventEmitter<{}> = new EventEmitter();
  is_bulk_order: boolean = false;
  input_format = (item: PersonItem) => item.display_value;
  private subscriptions: Subscription = new Subscription();
  private order_center_action_sub?: Subscription;

  constructor(
    private dashboard_svc: DashboardService,
    private modal_svc: ModalService,
    private person_svc: PersonService,
    private session_svc: SessionService,
    public translation_svc: TranslationService
  ) {
    this.order_for_options[0].name = this.translation_svc.translate('myself');
  }

  ngOnInit(): void {
    this.setOrderForOptions();
    this.setOrderCenterAction();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  isAuthorizedToPlaceOrder = (): boolean => this.session_svc.getAuthorizedToPlaceOrder();

  orderForOthers(for_display: boolean = false): boolean {
    const ofo: boolean =
      this.order_for_options.length > 1 && this.order_for_options[1].name === this.order_for;

    if (for_display) return ofo && this.person.value;
    else return ofo;
  }

  onSearchBlur(event: FocusEvent): void {
    const input: HTMLInputElement = event.target as HTMLInputElement;

    // On blur the typeahead clears the visible text (e.g. Alt on Windows moves focus to the
    // browser menu bar) but leaves its suggestion popup open with stale results. Re-dispatching
    // 'input' on the now-empty field makes the search stream emit [] so ngbTypeahead closes the
    // popup. Deferred so it lands after ngbTypeahead's own blur handler resubscribes the stream.
    // The guard runs inside the callback: a suggestion click can select a person between blur
    // and the timer, and dispatching then would wipe the selection and re-trigger a search.
    setTimeout(() => {
      if (this.person.value || input.getAttribute('aria-expanded') !== 'true') return;

      input.dispatchEvent(new Event('input'));
    }, 0);
  }

  onSelectPerson(event: NgbTypeaheadSelectItemEvent<any>): void {
    if (event.item) this.person.disable();
  }

  clearPerson(): void {
    this.person.reset('', { emitEvent: true });
    this.person.enable();
    this.person_not_found = '';
    this.loading = false;
  }
  selectOrderFor(name: string): void {
    this.order_for = name;
    switch (name) {
      case this.order_for_options[0].name:
        this.goToStep(this.select_activity_template, 3);
        break;

      case this.order_for_options[1].name:
        this.goToStep(this.select_person_template, 2);
        break;

      default:
        break;
    }
  }

  goToStep(content: TemplateRef<any>, step_id: number): void {
    this.step_id.setValue(step_id);
    this.setOrderCenterAction();

    this.resetTemplate();

    setTimeout(() => {
      this.modal_svc.open(content, {
        backdrop: 'static',
        centered: true
      });
    }, 0);
  }

  selectAction(orderActionType: OrderActionType): void {
    this.order_action_type.emit({
      orderActionType,
      person: this.orderForOthers() || this.is_bulk_order ? this.person.value : undefined
    });
  }

  resetTemplate(): void {
    this.person_not_found = '';
    this.loading = this.no_activity_found = false;
    this.modal_svc.dismissLastModal('Close click');
  }

  search: OperatorFunction<string, readonly Partial<PersonItem>[]> = (term$: Observable<string>) =>
    term$.pipe(
      debounceTime(100),
      tap(() => {
        this.person_not_found = '';
        this.loading = this.no_activity_found = false;
      }),
      distinctUntilChanged(),
      switchMap((term: string) => {
        const trimmed_term: string = term.trim().replace(/\s+/g, ' ');

        if (!trimmed_term) {
          if (term.length > 0)
            this.person_not_found = this.translation_svc.translate('person_not_found');

          return of([]);
        }

        if (trimmed_term.length < 3) return of([]);

        this.loading = true;
        return this.person_svc.search(trimmed_term).pipe(
          map((response: PersonSearchOptions) => {
            this.loading = false;

            if (response.items.length > 0) {
              return response.items.map((item: PersonItem) => ({
                ...item,
                display_value: `${item.person_name} (${item.email})`
              }));
            }

            this.person_not_found = this.translation_svc.translate('person_not_found');
            return [];
          }),
          catchError(() => {
            this.resetTemplate();
            return of([]);
          })
        );
      })
    );

  private setOrderForOptions(): void {
    if (this.ofo_enabled && !this.is_bulk_order) {
      const ofo_option: SelectOrderForItem = {
        name: this.translation_svc.translate('someone_else'),
        icon: 'supervisor_account'
      };
      this.order_for_options = [...this.order_for_options, ofo_option];
    }
  }

  private setOrderCenterAction(): void {
    this.no_activity_found = false;

    if (!this.orderForOthers() && !this.is_bulk_order) {
      this.order_center_action.order_types = this.getOrderTypes(
        this.my_order_center_actions,
        this.order_center_action
      );

      return;
    }

    if ((this.orderForOthers() || this.is_bulk_order) && this.step_id.value === 3) {
      const params: OrderActionParams = this.person.value?.id && {
        userId: this.person.value.id
      };
      this.order_center_action_sub?.unsubscribe();
      this.order_center_action_sub = this.dashboard_svc
        .orderCenterActions(params, 'enabled-activities')
        .subscribe({
          next: (res: OrderCenterResponse) => {
            this.order_center_action.order_types = this.getOrderTypes(
              res.order_groups,
              this.order_center_action
            );
            this.no_activity_found = !this.order_center_action.order_types;
          },
          error: (err: HttpErrorResponse) => {
            this.resetTemplate();
          }
        });
    }
  }

  private getOrderTypes(
    x: OrderCenterAction[],
    y: OrderCenterAction
  ): OrderCenterAction['order_types'] {
    return x.find((item: OrderCenterAction) => item.category === y.category)?.order_types!;
  }
}
